import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { COTIZ_ESTADOS, COTIZ_ESTADOS_LIST, type CotizEstado } from './CotizacionDetalle';
import { openCotizMailto } from '../../lib/cotizMailto';
import { toast, confirmar } from '../../lib/toast';

type Cotizacion = {
  id: string;
  fecha: string;
  unidades: number | null;
  coste_usd: number | string | null;
  cambio_usd_eur: number | string | null;
  foc_extra_pct: number | string | null;
  foc_proveedor_pct: number | string | null;
  aranceles_pct: number | string | null;
  rappel_pct: number | string | null;
  marketing_pct: number | string | null;
  comision_pm_pct: number | string | null;
  coste_video_eur: number | string | null;
  coste_traduccion_eur: number | string | null;
  provisiones_eur: number | string | null;
  bolsa_eur: number | string | null;
  cdpe_eur: number | string | null;
  coste_real_eur: number | string | null;
  coste_game_final_eur: number | string | null;
  pvpr_iva_eur: number | string | null;
  ref_proveedor: string | null;
  nombre_propuesto: string | null;
  notas: string | null;
  estado: CotizEstado;
  producto_id: string | null;
  subcategoria_id: string | null;
  proveedor_id: string | null;
  cliente_id: string | null;
  categoria_id: string | null;
  cliente_codigo: string | null;
  marca_id: string | null;
  marca_codigo: string | null;
  marca_nombre: string | null;
  subcategoria_nombre: string | null;
  categoria_nombre: string | null;
  detalle_nombre: string | null;
  proveedor_nombre: string | null;
  proveedor_email: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  main_specs: string | null;
  opciones_snapshot: Array<{ descripcion?: string | null; importe_usd?: number | string | null; incluido?: boolean }> | null;
  revision_pendiente: boolean;
  created_at: string;
};

function uniqueOptions<T extends Record<string, unknown>>(
  rows: T[] | undefined,
  idKey: keyof T,
  nameKey: keyof T,
): { value: string; label: string }[] {
  if (!rows) return [];
  const seen = new Map<string, string>();
  for (const r of rows) {
    const id = r[idKey];
    const nm = r[nameKey];
    if (typeof id === 'string' && id && typeof nm === 'string' && nm) {
      if (!seen.has(id)) seen.set(id, nm);
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/** Margen real Approx sobre venta neta. Misma fórmula que el card
 *  "Margen real Approx" del detalle: descuenta rappel+comisión+mkt%
 *  del precio facturado y los costes vídeo+traducción por unidad, y
 *  divide entre la venta neta. El coste real para Approx es CDPe +
 *  provisiones/u − bolsa/u (las provisiones a recuperar son coste real,
 *  la bolsa lo reduce). Devuelve % o null si no se puede calcular. */
function margenApproxRow(r: Cotizacion): number | null {
  const precio = Number(r.coste_game_final_eur);
  const cdpe = Number(r.cdpe_eur);
  if (!Number.isFinite(precio) || !Number.isFinite(cdpe) || precio <= 0) return null;
  const desc = ((Number(r.rappel_pct) || 0) + (Number(r.comision_pm_pct) || 0) + (Number(r.marketing_pct) || 0)) / 100;
  const uds = Number(r.unidades) || 0;
  const extras = uds > 0 ? ((Number(r.coste_video_eur) || 0) + (Number(r.coste_traduccion_eur) || 0)) / uds : 0;
  const ventaNeta = precio - precio * desc - extras;
  if (ventaNeta <= 0) return null;
  const provUnidad = uds > 0 ? (Number(r.provisiones_eur) || 0) / uds : 0;
  const bolsaUnidad = uds > 0 ? (Number(r.bolsa_eur) || 0) / uds : 0;
  const costeRealApprox = cdpe + provUnidad - bolsaUnidad;
  return ((ventaNeta - costeRealApprox) / ventaNeta) * 100;
}

type CotizacionesListProps = {
  clienteCodigo?: string;
  title?: string;
  calcRoute?: string;
};

export function CotizacionesList({ clienteCodigo, title, calcRoute }: CotizacionesListProps = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const clienteFilter = clienteCodigo ? `&cliente_codigo=${encodeURIComponent(clienteCodigo)}` : '';
  const listado = useQuery({
    queryKey: ['cotizaciones', 'listado-full', clienteCodigo ?? 'todas'],
    queryFn: () => api.get<{ data: Cotizacion[] }>(`/precio/cotizaciones/listado/full?limit=500${clienteFilter}`),
  });
  const rows = listado.data?.data;

  // Ocultar las cotizaciones en estado Descartado (marcado por defecto). La
  // selección/bulk usa `rows` (lista completa) pero la tabla solo ve las
  // visibles, así que las descartadas no se pueden seleccionar mientras esté on.
  const [ocultarDescartadas, setOcultarDescartadas] = useState(true);
  const descartadasCount = useMemo(() => rows?.filter((r) => r.estado === 'DESCARTADO').length ?? 0, [rows]);
  const visibleRows = useMemo(
    () => (ocultarDescartadas ? rows?.filter((r) => r.estado !== 'DESCARTADO') : rows),
    [rows, ocultarDescartadas],
  );

  const del = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/precio/cotizaciones/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cotizaciones'] }),
  });

  // ── Nueva cotización ──────────────────────────────────────────────
  const [newOpen, setNewOpen] = useState(false);
  const [newStep, setNewStep] = useState<'choose' | 'search'>('choose');
  const [ppSearch, setPpSearch] = useState('');
  type PPRow = { id: string; nombre: string; ref_proveedor: string; proveedor_nombre: string; gama_nombre: string | null; subcategoria_nombre: string | null };
  const ppList = useQuery({
    queryKey: ['catalogo', 'nueva-cotiz-search', ppSearch],
    queryFn: () => api.get<{ data: PPRow[] }>(`/productos-proveedor/listado/full?q=${encodeURIComponent(ppSearch)}&limit=20`),
    enabled: newStep === 'search',
  });

  // ── Selección + bulk ───────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEstado, setBulkEstado] = useState<CotizEstado | ''>('');

  const bulkChangeEstado = useMutation({
    mutationFn: (body: { ids: string[]; estado: CotizEstado }) =>
      api.post<{ actualizados: number }>('/precio/cotizaciones/bulk', { action: 'cambiar_estado', ...body }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['cotizaciones'] });
      setSelectedIds(new Set());
      setBulkEstado('');
      toast.success(`Actualizadas ${r.actualizados} cotización(es)`);
    },
    onError: (err) => toast.error('No se pudo cambiar el estado', (err as Error).message),
  });

  // ── Recálculo de cotizaciones (preview editable → crear nueva / actualizar in situ) ──
  type RecalcEdit = { coste_usd?: number; unidades?: number; cambio_usd_eur?: number; coste_transporte_unidad_eur?: number };
  type RecalcRow = {
    id: string; nombre: string | null; ref_proveedor: string | null;
    coste_usd: number; unidades: number; cambio_usd_eur: number; coste_transporte_unidad_eur: number;
    cdpe_eur: number; coste_real_eur: number; coste_game_final_eur: number; coste_game_final_old: number;
    margen_pct: number; margen_approx_real_pct: number | null; garantiza_margen_minimo: boolean;
    transporte_fuente?: 'producto' | 'detalle' | 'subcategoria' | 'categoria' | 'ninguno';
    precio_contenedor_eur?: number;
  };
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcRows, setRecalcRows] = useState<RecalcRow[]>([]);
  const [recalcEdits, setRecalcEdits] = useState<Record<string, { coste_usd: string; unidades: string; cambio: string; transporte: string }>>({});
  const [recalcDirty, setRecalcDirty] = useState(false);
  const [recalcCtx, setRecalcCtx] = useState<{ cambio_usd_eur: number; precio_contenedor_eur: number } | null>(null);

  const recalcPreview = useMutation({
    mutationFn: (body: { ids: string[]; overrides?: Record<string, RecalcEdit> }) =>
      api.post<{ data: RecalcRow[]; contexto: { cambio_usd_eur: number; precio_contenedor_eur: number } }>('/precio/cotizaciones/bulk-actualizar/preview', body),
    onSuccess: (r) => {
      setRecalcRows(r.data);
      setRecalcCtx(r.contexto);
      setRecalcDirty(false);
      setRecalcEdits((prev) => {
        const next = { ...prev };
        for (const row of r.data) {
          next[row.id] = {
            coste_usd: String(row.coste_usd),
            unidades: String(row.unidades),
            cambio: String(row.cambio_usd_eur),
            transporte: String(row.coste_transporte_unidad_eur),
          };
        }
        return next;
      });
    },
    onError: (err) => toast.error('No se pudo calcular la previsualización', (err as Error).message),
  });

  const recalcApply = useMutation({
    mutationFn: (body: { in_place: boolean; items: Array<{ id: string } & RecalcEdit> }) =>
      api.post<{ in_place: boolean; count: number }>('/precio/cotizaciones/bulk-actualizar', body),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['cotizaciones'] });
      setSelectedIds(new Set());
      setRecalcOpen(false); setRecalcRows([]); setRecalcEdits({});
      toast.success(r.in_place
        ? `Actualizadas ${r.count} cotización(es) en su sitio`
        : `Creadas ${r.count} cotización(es) nueva(s) en Pdte. de enviar`);
    },
    onError: (err) => toast.error('No se pudo aplicar el recálculo', (err as Error).message),
  });

  const abrirRecalc = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setRecalcEdits({}); setRecalcRows([]); setRecalcDirty(false); setRecalcCtx(null);
    setRecalcOpen(true);
    recalcPreview.mutate({ ids });
  };

  const recalcNum = (s: string | undefined, fallback: number) => {
    const t = (s ?? '').toString().trim();
    if (t === '') return fallback;
    const n = Number(t.replace(',', '.'));
    return Number.isFinite(n) ? n : fallback;
  };
  const recalcItems = (): Array<{ id: string } & RecalcEdit> =>
    recalcRows.map((row) => {
      const e = recalcEdits[row.id];
      return {
        id: row.id,
        coste_usd: recalcNum(e?.coste_usd, row.coste_usd),
        unidades: recalcNum(e?.unidades, row.unidades),
        cambio_usd_eur: recalcNum(e?.cambio, row.cambio_usd_eur),
        coste_transporte_unidad_eur: recalcNum(e?.transporte, row.coste_transporte_unidad_eur),
      };
    });
  const recalcRecompute = () => {
    const overrides: Record<string, RecalcEdit> = {};
    for (const it of recalcItems()) { const { id, ...rest } = it; overrides[id] = rest; }
    recalcPreview.mutate({ ids: recalcRows.map((r) => r.id), overrides });
  };

  const aceptarYPedido = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ pedido_cliente_id: string; lineas_creadas: number; cotizaciones_actualizadas: number }>(
        '/precio/cotizaciones/bulk',
        { action: 'aceptar_y_pedido', ids },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['cotizaciones'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setSelectedIds(new Set());
      toast.success(`Pedido creado con ${r.lineas_creadas} línea(s)`, 'Te llevo al pedido.');
      // Navega a la zona del cliente correcto (la propia página de
      // cotizaciones se monta para GAME o TICNOVA, así que el clienteCodigo
      // disponible aquí es el adecuado).
      const base = clienteCodigo === 'TICNOVA' ? '/ticnova/pedidos' : '/game/pedidos';
      navigate(`${base}/${r.pedido_cliente_id}`);
    },
    onError: (err) => toast.error('No se pudo crear el pedido', (err as Error).message),
  });

  const aplicarBulkEstado = async () => {
    if (!bulkEstado || selectedIds.size === 0) return;
    if (!(await confirmar({ title: `¿Cambiar el estado de ${selectedIds.size} cotización(es)?`, message: `Pasarán a "${COTIZ_ESTADOS[bulkEstado].label}".`, confirmLabel: 'Cambiar estado' }))) return;
    bulkChangeEstado.mutate({ ids: Array.from(selectedIds), estado: bulkEstado });
  };

  const aceptarBulk = async () => {
    if (selectedIds.size === 0) return;
    if (!(await confirmar({ title: `¿Aceptar ${selectedIds.size} cotización(es)?`, message: 'Se generará un pedido en estado Pdte. de respuesta.', confirmLabel: 'Aceptar y crear pedido' }))) return;
    aceptarYPedido.mutate(Array.from(selectedIds));
  };

  const [emailPending, setEmailPending] = useState(false);
  const enviarEmailBulk = async () => {
    if (!rows || selectedIds.size === 0) return;
    const seleccionadas = rows.filter((r) => selectedIds.has(r.id));
    const items = seleccionadas.map((r) => ({
      producto_nombre: r.producto_nombre,
      nombre_propuesto: r.nombre_propuesto,
      ref_proveedor: r.ref_proveedor,
      subcategoria_nombre: r.subcategoria_nombre,
      detalle_nombre: r.detalle_nombre,
      unidades: r.unidades,
      coste_game_final_eur: r.coste_game_final_eur,
      main_specs: r.main_specs,
    }));
    setEmailPending(true);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const resp = await fetch('/api/precio/cotizaciones/export-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      let attachment: import('../../lib/cotizMailto').EmlAttachment | undefined;
      if (resp.ok) {
        const buf = await resp.arrayBuffer();
        attachment = {
          filename: `cotizaciones_${new Date().toISOString().slice(0, 10)}.xlsx`,
          mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          data: buf,
        };
      }
      openCotizMailto(items, { attachment });
    } catch {
      openCotizMailto(items);
    } finally {
      setEmailPending(false);
    }
  };

  const categoriaOptions = useMemo(() => uniqueOptions(rows, 'categoria_id', 'categoria_nombre'), [rows]);
  const subcategoriaOptions = useMemo(() => uniqueOptions(rows, 'subcategoria_id', 'subcategoria_nombre'), [rows]);
  const proveedorOptions = useMemo(() => uniqueOptions(rows, 'proveedor_id', 'proveedor_nombre'), [rows]);
  const marcaOptions = useMemo(() => uniqueOptions(rows, 'marca_id', 'marca_nombre'), [rows]);
  const estadoOptions = useMemo(() => COTIZ_ESTADOS_LIST.map((e) => ({ value: e, label: COTIZ_ESTADOS[e].label })), []);

  const columns: Column<Cotizacion>[] = useMemo(() => [
    { key: 'fecha', label: 'Fecha', render: (r) => <span className="text-xs">{fmt.datetime(r.fecha)}</span>, sortValue: (r) => r.fecha },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => {
        const meta = COTIZ_ESTADOS[r.estado] ?? { label: r.estado, color: '#64748b' };
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
      sortValue: (r) => r.estado,
    },
    {
      key: 'producto',
      label: 'Producto / ref',
      render: (r) => (
        <span className="flex items-center gap-1 text-sm">
          {r.revision_pendiente && (
            <span className="text-amber-600" title="Pdte. de revisar">⚠️</span>
          )}
          {r.producto_sku
            ? (
              <span className="truncate">
                <span className="font-mono text-xs">{r.producto_sku}</span>
                {r.producto_nombre && <span className="text-slate-700"> — {r.producto_nombre}</span>}
              </span>
            )
            : (r.nombre_propuesto || r.ref_proveedor || <span className="italic text-slate-400">—</span>)}
        </span>
      ),
      sortValue: (r) => r.producto_sku ?? r.nombre_propuesto ?? r.ref_proveedor ?? '',
      // La key 'producto' es sintética: sin searchValue el buscador no vería
      // ninguno de los campos que pinta esta columna (BUG-027).
      searchValue: (r) => [r.producto_sku, r.producto_nombre, r.nombre_propuesto, r.ref_proveedor]
        .filter(Boolean).join(' '),
    },
    { key: 'marca_nombre', label: 'Marca', render: (r) => <span className="text-xs">{r.marca_nombre ?? '—'}</span>, sortValue: (r) => r.marca_nombre ?? '' },
    { key: 'categoria_nombre', label: 'Categoría', render: (r) => <span className="text-xs">{r.categoria_nombre ?? '—'}</span>, sortValue: (r) => r.categoria_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subcategoría', render: (r) => <span className="text-xs">{r.subcategoria_nombre ?? '—'}</span>, sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'proveedor_nombre', label: 'Proveedor', render: (r) => <span className="text-xs">{r.proveedor_nombre ?? '—'}</span>, sortValue: (r) => r.proveedor_nombre ?? '' },
    { key: 'unidades', label: 'Uds.', align: 'right', render: (r) => <span className="font-mono text-xs">{r.unidades != null ? fmt.int(r.unidades) : '—'}</span>, sortValue: (r) => Number(r.unidades) },
    { key: 'coste_usd', label: 'Coste USD', align: 'right', render: (r) => <span className="font-mono text-xs">{fmt.usd(r.coste_usd)}</span>, sortValue: (r) => Number(r.coste_usd) },
    { key: 'foc_proveedor_pct', label: 'FOC prov.', align: 'right', render: (r) => <span className="font-mono text-xs">{fmt.pct(r.foc_proveedor_pct)}</span>, sortValue: (r) => Number(r.foc_proveedor_pct) },
    { key: 'coste_game_final_eur', label: 'Coste final', align: 'right', render: (r) => <span className="font-mono text-xs font-semibold">{fmt.eur(r.coste_game_final_eur)}</span>, sortValue: (r) => Number(r.coste_game_final_eur) },
    {
      key: 'margen_approx',
      label: 'Margen Approx',
      align: 'right',
      render: (r) => {
        const m = margenApproxRow(r);
        if (m == null) return <span className="text-slate-300">—</span>;
        const positivo = m >= 0;
        return (
          <span
            className={`font-mono text-xs font-semibold ${positivo ? 'text-emerald-700' : 'text-rose-700'}`}
            title="Margen sobre venta neta: (venta_neta − CDPe) / venta_neta × 100. Descuenta rappel + comisión PM + marketing y los costes de vídeo+traducción por unidad."
          >
            {fmt.pct(m)}
          </span>
        );
      },
      sortValue: (r) => {
        const m = margenApproxRow(r);
        return m == null ? -999 : m;
      },
    },
    { key: 'pvpr_iva_eur', label: 'PVPr c/IVA', align: 'right', render: (r) => <span className="font-mono text-xs font-semibold">{fmt.eur(r.pvpr_iva_eur)}</span>, sortValue: (r) => Number(r.pvpr_iva_eur) },
    {
      key: 'acciones',
      label: '',
      sortable: false,
      align: 'right',
      render: (r) => (
        <Button
          size="sm"
          variant="danger"
          onClick={async (e) => {
            e.stopPropagation();
            if (await confirmar({ title: '¿Eliminar esta cotización?', message: 'Se mantiene en el audit log.', confirmLabel: 'Eliminar', danger: true })) del.mutate(r.id);
          }}
          disabled={del.isPending}
        >
          🗑
        </Button>
      ),
    },
  ], [del]);

  return (
    <div>
      <PageHeader
        title={title ?? `Cotizaciones${clienteCodigo ? ` ${clienteCodigo}` : ''}`}
        subtitle="Histórico completo. Haz click en una fila para abrir su ficha; ahí podrás editar parámetros, cambiar estado y ver el registro de cambios."
        actions={
          <Button variant="create" onClick={() => { setNewOpen(true); setNewStep('choose'); }}>
            + Nueva cotización
          </Button>
        }
      />

      {/* ── Modal: Nueva cotización ─────────────────────────────────── */}
      <Modal
        open={newOpen}
        onClose={() => { setNewOpen(false); setPpSearch(''); }}
        title={newStep === 'choose' ? 'Nueva cotización' : 'Seleccionar producto de proveedor'}
        size={newStep === 'search' ? 'xl' : 'md'}
      >
        {newStep === 'choose' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">¿Cómo quieres empezar?</p>
            <Button
              variant="create"
              onClick={() => setNewStep('search')}
              className="w-full justify-center"
            >
              Desde un producto de proveedor existente
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setNewOpen(false); navigate(calcRoute ?? '/game/calculadora'); }}
              className="w-full justify-center"
            >
              Desde cero (calculadora vacía)
            </Button>
          </div>
        )}
        {newStep === 'search' && (
          <div className="space-y-3">
            <Input
              autoFocus
              placeholder="Buscar por nombre, ref. proveedor, descripción…"
              value={ppSearch}
              onChange={(e) => setPpSearch(e.target.value)}
            />
            <p className="text-[10px] leading-tight text-slate-400">
              Varias palabras = todas deben coincidir · <span className="font-medium">palabra1 / palabra2</span> = cualquiera coincide · <span className="font-medium">"frase exacta"</span> = coincidencia literal
            </p>
            <div className="max-h-[50vh] overflow-y-auto">
              {ppList.isFetching && <div className="py-4 text-center text-sm text-slate-400">Buscando…</div>}
              {!ppList.isFetching && (ppList.data?.data ?? []).length === 0 && (
                <div className="py-4 text-center text-sm text-slate-400">Sin resultados</div>
              )}
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <tbody className="divide-y divide-slate-50">
                  {(ppList.data?.data ?? []).map((pp) => (
                    <tr key={pp.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{pp.ref_proveedor}</td>
                      <td className="px-3 py-2">{pp.nombre}</td>
                      <td className="px-3 py-2 text-slate-500">{pp.proveedor_nombre}</td>
                      <td className="px-3 py-2 text-slate-400">{pp.subcategoria_nombre ?? pp.gama_nombre ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="create"
                          onClick={() => { setNewOpen(false); setPpSearch(''); navigate(`${calcRoute ?? '/game/calculadora'}?pp=${pp.id}`); }}
                        >
                          Cotizar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-start">
              <button type="button" onClick={() => setNewStep('choose')} className="cursor-pointer text-xs text-slate-500 hover:underline">
                ← Volver
              </button>
            </div>
          </div>
        )}
      </Modal>

      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
          <span className="font-semibold text-sky-900">
            {selectedIds.size} cotización{selectedIds.size === 1 ? '' : 'es'} seleccionada{selectedIds.size === 1 ? '' : 's'}
          </span>
          <div className="flex-1" />
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Cambiar estado a</label>
            <Select
              options={COTIZ_ESTADOS_LIST.map((e) => ({ value: e, label: COTIZ_ESTADOS[e].label }))}
              value={bulkEstado}
              onChange={(e) => setBulkEstado(e.target.value as CotizEstado | '')}
              emptyLabel="(elige)"
              className="w-64"
            />
          </div>
          <Button
            variant="save"
            onClick={aplicarBulkEstado}
            disabled={!bulkEstado || bulkChangeEstado.isPending}
          >
            {bulkChangeEstado.isPending ? 'Aplicando…' : 'Aplicar'}
          </Button>
          <Button
            variant="secondary"
            onClick={enviarEmailBulk}
            disabled={emailPending}
            title="Descarga un .eml con la tabla en HTML + Excel adjunto. Se abre en Outlook como borrador editable."
          >
            {emailPending ? '📧 Generando…' : '📧 Email'}
          </Button>
          <Button
            variant="secondary"
            onClick={abrirRecalc}
            disabled={recalcPreview.isPending || recalcApply.isPending}
            title="Abre una ventana con los nuevos valores (cambio, transporte, coste, unidades) editables, calcula costes y márgenes, y permite crear cotizaciones nuevas o actualizar las actuales en su sitio."
          >
            ↻ Actualizar / recalcular (cambio · transporte · coste)
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const { data: sess } = await (await import('../../lib/supabase')).supabase.auth.getSession();
                const token = sess.session?.access_token;
                const resp = await fetch('/api/precio/cotizaciones/export-excel', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                  body: JSON.stringify({ ids: Array.from(selectedIds) }),
                });
                if (!resp.ok) throw new Error(`Error ${resp.status}`);
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `cotizaciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error('No se pudo generar el Excel', (err as Error).message);
              }
            }}
            title="Descarga un Excel con imagen, specs, MOQ, coste, PVPr y margen de las cotizaciones seleccionadas."
          >
            📊 Excel
          </Button>
          <Button
            variant="create"
            onClick={aceptarBulk}
            disabled={aceptarYPedido.isPending}
            title="Marca como ACEPTADA y genera un pedido cliente PDTE_RESPUESTA con una línea por cotización."
          >
            ✓ {aceptarYPedido.isPending ? 'Procesando…' : 'Aceptar y pasar a pedido'}
          </Button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
          >
            Limpiar selección
          </button>
        </div>
      )}

      <div className="mb-2 flex items-center justify-end">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={ocultarDescartadas}
            onChange={(e) => setOcultarDescartadas(e.target.checked)}
            className="h-4 w-4 cursor-pointer"
          />
          Ocultar cotizaciones Descartadas{descartadasCount > 0 ? ` (${descartadasCount})` : ''}
        </label>
      </div>

      <DataTable<Cotizacion>
        columns={columns}
        rows={visibleRows}
        loading={listado.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => {
          // Navega a la zona del cliente correcto: TICNOVA → /ticnova,
          // resto → /game. Asi la URL del detalle queda coherente con
          // la ruta donde se monta el listado.
          const base = clienteCodigo === 'TICNOVA' ? '/ticnova' : '/game';
          navigate(`${base}/cotizaciones/${r.id}`);
        }}
        defaultSort={{ key: 'fecha', desc: true }}
        searchPlaceholder="Buscar por SKU, nombre, ref. proveedor…"
        stickyHeader
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        filters={[
          { key: 'estado', label: 'Estado', options: estadoOptions },
          { key: 'marca_id', label: 'Marca', options: marcaOptions },
          { key: 'categoria_id', label: 'Categoría', options: categoriaOptions },
          { key: 'subcategoria_id', label: 'Subcategoría', options: subcategoriaOptions },
          { key: 'proveedor_id', label: 'Proveedor', options: proveedorOptions },
          { key: 'revision_pendiente', label: '⚠️ Pdte. revisar', type: 'boolean' as const, getValue: (r: Cotizacion) => Boolean(r.revision_pendiente) },
        ]}
      />

      {/* Modal: Recalcular cotizaciones — preview editable + crear/actualizar */}
      <Modal
        open={recalcOpen}
        onClose={() => setRecalcOpen(false)}
        title={`Recalcular · ${recalcRows.length || selectedIds.size} cotización(es)`}
        size="xl"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <Button variant="secondary" onClick={recalcRecompute} disabled={recalcPreview.isPending || recalcRows.length === 0}>
              {recalcPreview.isPending ? '↻ Calculando…' : '↻ Recalcular previsualización'}
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="danger" onClick={() => setRecalcOpen(false)}>Cancelar</Button>
              <Button
                variant="secondary"
                onClick={() => recalcApply.mutate({ in_place: false, items: recalcItems() })}
                disabled={recalcApply.isPending || recalcRows.length === 0}
                title="Crea cotizaciones NUEVAS con estos valores; las originales se mantienen."
              >
                {recalcApply.isPending ? 'Aplicando…' : '＋ Crear nuevas'}
              </Button>
              <Button
                variant="create"
                onClick={async () => {
                  if (!(await confirmar({ title: `¿Actualizar en su sitio ${recalcRows.length} cotización(es)?`, message: 'Se sobrescriben los valores actuales (las originales no se conservan).', confirmLabel: 'Actualizar', danger: true }))) return;
                  recalcApply.mutate({ in_place: true, items: recalcItems() });
                }}
                disabled={recalcApply.isPending || recalcRows.length === 0}
                title="Sobrescribe las cotizaciones seleccionadas con estos valores (sin crear copias)."
              >
                {recalcApply.isPending ? 'Aplicando…' : '↻ Actualizar las actuales'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <p className="max-w-[60%] text-xs text-slate-500">
            Edita coste, unidades, cambio y transporte si quieres; pulsa <strong>↻ Recalcular</strong> para refrescar costes y márgenes. Luego elige crear cotizaciones nuevas o actualizar las actuales. Si la garantía está marcada y el coste final ya cumple el margen mínimo, se conserva; solo sube si quedaría por debajo.
          </p>
          {recalcCtx && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-right text-xs">
              <div className="text-slate-500">Cambio aplicado: <span className="font-mono font-semibold text-slate-800">{recalcCtx.cambio_usd_eur.toFixed(2)}</span> USD→€</div>
              <div className="text-slate-500">Precio contenedor: <span className="font-mono font-semibold text-slate-800">{recalcCtx.precio_contenedor_eur.toFixed(2)} €</span></div>
            </div>
          )}
        </div>
        {recalcDirty && (
          <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
            Has editado valores: pulsa <strong>↻ Recalcular previsualización</strong> para ver los costes y márgenes actualizados antes de aplicar.
          </p>
        )}
        <div className="max-h-[60vh] overflow-y-auto">
          {recalcPreview.isPending && recalcRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Calculando previsualización…</p>
          ) : (
            <table className="min-w-full divide-y divide-slate-100 text-xs">
              <thead className="sticky top-0 bg-slate-50 uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-2 text-left">Producto / Ref</th>
                  <th className="px-2 py-2 text-right">Coste USD</th>
                  <th className="px-2 py-2 text-right">Uds</th>
                  <th className="px-2 py-2 text-right">Cambio</th>
                  <th className="px-2 py-2 text-right">Transp. €</th>
                  <th className="px-2 py-2 text-right">CDPe</th>
                  <th className="px-2 py-2 text-right">Coste real</th>
                  <th className="px-2 py-2 text-right">Coste final</th>
                  <th className="px-2 py-2 text-right">Margen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recalcRows.map((row) => {
                  const e = recalcEdits[row.id] ?? { coste_usd: '', unidades: '', cambio: '', transporte: '' };
                  const setE = (k: 'coste_usd' | 'unidades' | 'cambio' | 'transporte', val: string) => {
                    setRecalcDirty(true);
                    setRecalcEdits((s) => ({ ...s, [row.id]: { ...s[row.id], [k]: val } }));
                  };
                  const finalSubio = row.coste_game_final_old > 0 && Math.abs(row.coste_game_final_eur - row.coste_game_final_old) >= 0.005;
                  return (
                    <tr key={row.id}>
                      <td className="px-2 py-1">
                        <div className="max-w-[180px] truncate" title={row.nombre ?? ''}>{row.nombre ?? '—'}</div>
                        <div className="font-mono text-[10px] text-slate-400">{row.ref_proveedor ?? ''}</div>
                      </td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="any" value={e.coste_usd} onChange={(ev) => setE('coste_usd', ev.target.value)} className="w-20 text-right font-mono" />
                      </td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" value={e.unidades} onChange={(ev) => setE('unidades', ev.target.value)} className="w-16 text-right font-mono" />
                      </td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="0.0001" value={e.cambio} onChange={(ev) => setE('cambio', ev.target.value)} className="w-20 text-right font-mono" />
                      </td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="any" value={e.transporte} onChange={(ev) => setE('transporte', ev.target.value)} className="w-16 text-right font-mono" />
                        {row.transporte_fuente && row.transporte_fuente !== 'ninguno' && (
                          <div className="mt-0.5 text-[9px] leading-tight text-slate-400" title="Origen del coste de transporte (cascada producto → detalle → subcategoría → categoría)">
                            de {row.transporte_fuente === 'subcategoria' ? 'subcategoría' : row.transporte_fuente === 'categoria' ? 'categoría' : row.transporte_fuente}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">{row.cdpe_eur.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right font-mono">{row.coste_real_eur.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right font-mono">
                        {finalSubio && <span className="mr-1 text-[10px] text-slate-400 line-through">{row.coste_game_final_old.toFixed(2)}</span>}
                        <span className={finalSubio ? 'font-semibold text-emerald-700' : ''}>{row.coste_game_final_eur.toFixed(2)}</span>
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {row.margen_approx_real_pct != null ? `${row.margen_approx_real_pct.toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Modal>
    </div>
  );
}
